import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { oauthAPI } from '../services/api';

const OAuthContext = createContext();

export const OAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('oauth_user');
    const storedToken = localStorage.getItem('oauth_token');

    if (storedUser && storedToken) {
      try {
        const user = JSON.parse(storedUser);
        setCurrentUser(user);
        verifyUser().catch(err => {
          console.error('Error verifying user on mount:', err);
        });
      } catch (err) {
        console.error('Error loading OAuth user:', err);
        clearUser();
      }
    }
    setIsInitialized(true);
  }, []);

  const verifyUser = async () => {
    try {
      const response = await oauthAPI.getMe();
      if (response.success) {
        const user = response.user;
        setCurrentUser(user);
        localStorage.setItem('oauth_user', JSON.stringify(user));

        const hasActiveCharacter = user.activeCharacterId ||
          (user.characters && user.characters.some(c => c.isActive));

        if (!hasActiveCharacter && user.characters && user.characters.length > 0) {
          return true;
        }

        return true;
      } else {
        clearUser();
        return false;
      }
    } catch (err) {
      console.error('Error verifying OAuth user:', err);
      clearUser();
      return false;
    }
  };

  const clearUser = () => {
    setCurrentUser(null);
    localStorage.removeItem('oauth_user');
    localStorage.removeItem('oauth_token');
  };

  const login = async (redirectUrl = null) => {
    try {
      const currentPath = redirectUrl || window.location.pathname + window.location.search;
      sessionStorage.setItem('oauth_redirect_url', currentPath);

      const response = await oauthAPI.getLoginUrl();
      if (response.success) {
        window.location.href = response.authUrl;
        return true;
      } else {
        setError(response.message || 'Failed to get login URL');
        return false;
      }
    } catch (err) {
      setError(err.message || 'Failed to initiate login');
      return false;
    }
  };

  const handleOAuthCallback = async (code) => {
    setLoading(true);
    setError(null);
    try {
      if (code === 'debug') {
        const storedUser = localStorage.getItem('oauth_user');
        const storedToken = localStorage.getItem('oauth_token');
        if (storedUser && storedToken) {
          const user = JSON.parse(storedUser);
          setCurrentUser(user);

          const hasActiveCharacter = user.activeCharacterId ||
            (user.characters && user.characters.some(c => c.isActive));

          return {
            success: true,
            needsCharacterSelection: user.characters && user.characters.length > 0 && !hasActiveCharacter,
            isNewUser: false
          };
        } else {
          setError('Debug login failed - no user data found');
          return { success: false };
        }
      }

      const response = await oauthAPI.handleCallback(code);
      if (response.success) {
        localStorage.setItem('oauth_token', response.token);
        localStorage.setItem('oauth_user', JSON.stringify(response.user));
        setCurrentUser(response.user);

        if (response.user.roleId && response.user.permissions) {
          window.dispatchEvent(new Event('oauthUserUpdated'));
        }

        const user = response.user;

        const activeCharacterExists = user.activeCharacterId !== null &&
                                     user.activeCharacterId !== undefined &&
                                     user.characters &&
                                     user.characters.some(c => c.id === user.activeCharacterId);

        const hasActiveCharacterFlag = user.characters &&
                                      user.characters.some(c => c.isActive === true || c.isActive === 1);

        const hasActiveCharacter = activeCharacterExists || hasActiveCharacterFlag;
        const needsSelection = user.characters && user.characters.length > 0 && !hasActiveCharacter;

        console.log('OAuth callback - character check:', {
          activeCharacterId: user.activeCharacterId,
          activeCharacterExists,
          hasActiveCharacterFlag,
          hasActiveCharacter,
          needsSelection,
          hasRole: !!user.roleId,
          characters: user.characters?.map(c => ({ id: c.id, isActive: c.isActive }))
        });

        return {
          success: true,
          needsCharacterSelection: needsSelection || response.needsCharacterSelection || false,
          isNewUser: response.isNewUser || false
        };
      } else {
        setError(response.message || 'OAuth callback failed');
        return { success: false };
      }
    } catch (err) {
      setError(err.message || 'OAuth callback error');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const selectCharacter = async (characterId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await oauthAPI.selectCharacter(characterId);
      if (response.success) {
        const verified = await verifyUser();
        return verified;
      } else {
        setError(response.message || 'Failed to select character');
        return false;
      }
    } catch (err) {
      setError(err.message || 'Failed to select character');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(() => {
    clearUser();
  }, []);

  const value = {
    currentUser,
    isInitialized,
    loading,
    error,
    login,
    handleOAuthCallback,
    selectCharacter,
    logout,
    verifyUser
  };

  return (
    <OAuthContext.Provider value={value}>
      {children}
    </OAuthContext.Provider>
  );
};
export const useOAuth = () => {
  const context = useContext(OAuthContext);
  if (!context) {
    throw new Error('useOAuth must be used within an OAuthProvider');
  }
  return context;
};

